import React, { useEffect, useMemo, useState } from 'react';

import { MultiSelect } from 'primereact/multiselect';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FILTER_TIMELAPSE_TIME,
  VISUALIZATION_OPTIONS,
  VisualizationFormState,
  VisualizationType,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { OptionType } from 'interfaces/shared.interface';
import { SelectFormInput, Input } from 'shared-components';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';

type Props = {
  control: any;
  selectedTable: string | null;
  selectedColumnsData: ReportingColumn[];
  onChange: (data: VisualizationFormState | null) => void;
  groupingColumn?: any;
  aggregateColumns?: { label: string; value: string; rawColumn?: string }[];
  onConfigChange?: () => void;
  initialVisualization?: VisualizationFormState | null;
  selectedVisualizationType?: VisualizationType | null;
  selectedVisualizationColumn?: string;
  selectedVisualizationColumns?: string[];
  selectedTimelapseType?: number;
};

const CreateReportTemplateVisualizationBlock: React.FC<Props> = ({
  control,
  selectedTable,
  selectedColumnsData,
  onChange,
  groupingColumn,
  aggregateColumns,
  onConfigChange,
  selectedVisualizationType,
  selectedVisualizationColumn,
  selectedVisualizationColumns,
  selectedTimelapseType,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<VisualizationType | null>(null);
  const [, setColumn] = useState('');
  const { setValue } = useFormContext();
  const [selectedColumnMeta, setSelectedColumnMeta] = useState<ReportingColumn | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [rowLimit, setRowLimit] = useState(50);
  const [timelapseType, setTimelapseType] = useState<number>(2);
  const [isInitialized, setIsInitialized] = useState(false);

  const DEFAULT_TIME_TYPE = FILTER_TIMELAPSE_TIME.find((opt) => opt.value === 2) ?? { value: 2, label: 'Monthly' };

  const availableColumns: OptionType[] = useMemo(() => {
    const groupOptions = Array.isArray(groupingColumn)
      ? groupingColumn.map((col) => ({ label: col, value: col }))
      : groupingColumn
        ? [{ label: groupingColumn, value: groupingColumn }]
        : [];

    const aggOptions = aggregateColumns ?? [];

    if (groupOptions.length || aggOptions.length) {
      return [...groupOptions, ...aggOptions];
    }

    return selectedColumnsData
      .filter((col) => col.tableName === selectedTable)
      .map((col) => ({ label: col.name, value: col.name }));
  }, [groupingColumn, aggregateColumns, selectedColumnsData, selectedTable]);

  const handleTypeChange = (selected: OptionType | null) => {
    const selectedType = selected?.value ?? null;
    setType(selectedType);
    setColumn('');
    setColumns([]);
    setSelectedColumnMeta(null);
    setTimelapseType(2);

    if (!selectedType) {
      onChange(null);
      return;
    }

    if (selectedType === 'Table') {
      onChange({ type: 'Table', columns: [], rowLimit: 0 });
    } else {
      onChange({ type: selectedType, column: '' });
    }
  };

  const handleSingleColumnChange = (selected: OptionType | null) => {
    const val = selected?.value ?? '';
    const columnMeta = selectedColumnsData.find((col) => col.name === val && col.tableName === selectedTable) || null;

    if (type && type !== 'Table') {
      const updated: VisualizationFormState = {
        type,
        column: val,
        ...(columnMeta?.columnType === 5 ? { timelapseType } : {}),
      };
      onChange(updated);
    }

    setSelectedColumnMeta(columnMeta);
  };

  const handleTimelapseTypeChange = (selected: OptionType | null) => {
    const timelapseValue = selected?.value ? Number(selected.value) : DEFAULT_TIME_TYPE.value;
    setTimelapseType(timelapseValue);

    if (type && type !== 'Table' && selectedColumnMeta) {
      const updated: VisualizationFormState = {
        type,
        column: selectedColumnMeta.name,
        timelapseType: timelapseValue,
      };

      onChange(updated);
    }
  };

  const handleRowLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (raw === '') {
      setRowLimit(NaN);
      if (type === 'Table') {
        onChange({ type: 'Table', columns, rowLimit: undefined });
      }
      return;
    }

    const value = Number(raw);
    if (!isNaN(value)) {
      setRowLimit(value);
      if (type === 'Table') {
        onChange({ type: 'Table', columns, rowLimit: value });
      }
    }
  };

  const filteredGroupedOptions = useMemo(() => {
    if (!selectedTable) {
      return [];
    }

    let availableColumns = selectedColumnsData
      .filter((col) => col.tableName === selectedTable)
      .map((col) => ({ label: col.name, value: col.name }));

    if (Array.isArray(groupingColumn) && groupingColumn.length > 0) {
      availableColumns = availableColumns.filter((col) => groupingColumn.includes(col.value));
    } else if (groupingColumn && typeof groupingColumn === 'string') {
      availableColumns = availableColumns.filter((col) => col.value === groupingColumn);
    }

    if (aggregateColumns?.length) {
      const aggOptions = aggregateColumns.map(({ label, value }) => ({ label, value }));
      availableColumns = [...availableColumns, ...aggOptions];
    }

    return [
      {
        label: selectedTable,
        items: availableColumns,
      },
    ];
  }, [selectedColumnsData, selectedTable, groupingColumn, aggregateColumns]);

  useEffect(() => {
    if (!selectedColumnMeta) {
      onConfigChange?.();
      return;
    }

    const stillExists = selectedColumnsData.some(
      (col) => col.name === selectedColumnMeta.name && col.tableName === selectedColumnMeta.tableName
    );

    if (!stillExists && type && type !== 'Table') {
      setSelectedColumnMeta(null);
      onChange({ type, column: '' });
    }

    onConfigChange?.();
  }, [selectedTable, groupingColumn, aggregateColumns, selectedColumnMeta]);

  useEffect(() => {
    if (isInitialized || !selectedVisualizationType) {
      return;
    }

    setType(selectedVisualizationType);

    if (selectedVisualizationType === 'Table') {
      const cols = selectedVisualizationColumns ?? [];
      setColumns(cols);

      setValue('visualizationType', { label: 'Table', value: 'Table' });
      setValue(
        'visualizationColumns',
        cols.map((col) => ({ label: col, value: col }))
      );

      onChange({
        type: 'Table',
        columns: cols,
      });
    } else {
      const column = selectedVisualizationColumn ?? '';
      const meta = selectedColumnsData.find((col) => col.name === column && col.tableName === selectedTable);
      setSelectedColumnMeta(meta ?? null);

      if (selectedTimelapseType) {
        setTimelapseType(selectedTimelapseType);
      }

      setValue('visualizationType', { label: selectedVisualizationType, value: selectedVisualizationType });
      if (column === '') {
        setValue('visualizationColumn', null);
        setColumn('');
      } else {
        setValue('visualizationColumn', { label: column, value: column });
        setColumn(column);
      }
      if (selectedTimelapseType) {
        setValue('timelapseType', { label: 'Monthly', value: selectedTimelapseType.toString() });
      }

      onChange({
        type: selectedVisualizationType,
        column,
        ...(selectedTimelapseType ? { timelapseType: selectedTimelapseType } : {}),
      });
    }

    setIsInitialized(true);
  }, [
    isInitialized,
    selectedVisualizationType,
    selectedVisualizationColumn,
    selectedVisualizationColumns,
    selectedTimelapseType,
    selectedTable,
  ]);

  useEffect(() => {
    if (
      isInitialized &&
      selectedVisualizationType &&
      selectedVisualizationType !== 'Table' &&
      selectedVisualizationColumn === ''
    ) {
      setValue('visualizationColumn', null);
      setColumn('');
      setSelectedColumnMeta(null);
      onChange({
        type: selectedVisualizationType,
        column: '',
        ...(timelapseType ? { timelapseType } : {}),
      });
    }
  }, [selectedVisualizationColumn, isInitialized, selectedVisualizationType]);

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800 py-5 flex items-center justify-between flex-col">
      <SelectFormInput
        name="visualizationType"
        control={control}
        options={VISUALIZATION_OPTIONS}
        label={t('Visualization')}
        labelClassName="mb-2 text-black-900 dark:text-white-00"
        placeholder={t('SelectType')}
        required
        onSelectValue={handleTypeChange}
        menuPlacement="top"
      />

      {type && type !== 'Table' && (
        <>
          <SelectFormInput
            className="mt-4"
            labelClassName="mb-2 text-black-900 dark:text-white-00"
            label={t('Column')}
            required
            name="visualizationColumn"
            control={control}
            options={availableColumns}
            placeholder={t('SelectСolumn')}
            onSelectValue={handleSingleColumnChange}
            menuPlacement="top"
          />
          {selectedColumnMeta?.columnType === 5 && (
            <SelectFormInput
              className="mt-4"
              labelClassName="mb-2 text-black-900 dark:text-white-00"
              label={t('TimelapseType')}
              required
              name="timelapseType"
              control={control}
              options={FILTER_TIMELAPSE_TIME.map(({ value, label }) => ({ value: value.toString(), label }))}
              onSelectValue={handleTimelapseTypeChange}
              menuPlacement="top"
              value={timelapseType.toString()}
              defaultValue={DEFAULT_TIME_TYPE.value.toString()}
            />
          )}
        </>
      )}
      {type === 'Table' && (
        <>
          <div className="w-full mt-4">
            <label className="text-sm text-black-900 dark:text-white-00 mb-2 block">
              {t('Columns')}
              <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              value={columns}
              options={filteredGroupedOptions}
              onChange={(e: any) => {
                const values = e.value || [];
                setColumns(values);
                onChange({ type: 'Table', columns: values, rowLimit });
              }}
              optionLabel="label"
              optionValue="value"
              optionGroupLabel="label"
              optionGroupChildren="items"
              display="chip"
              filter
              placeholder={t('SelectColumns')}
              panelClassName="custom-multiselect-panel"
              className="w-full border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>
          <Input
            className="mt-4 w-full"
            labelClassName="mb-2 text-black-900 dark:text-white-00 text-sm"
            inputClassName="!text-sm"
            label={t('RowLimit')}
            name="rowLimit"
            type="number"
            value={rowLimit}
            onChange={handleRowLimitChange}
            placeholder="Row Limit"
            defaultValue={50}
          />
        </>
      )}
    </div>
  );
};

export default CreateReportTemplateVisualizationBlock;
